import { makeAutoObservable, runInAction } from "mobx";
import { getRoot } from "mobx-easy";

import SubscriptionService from "../../services/SubscriptionService";

export default class SubscriptionStore {
	subscriptionStatus = "";
	subscriptionEnd = undefined;
	subscriptionQuantity = 0;
	nextCycleQuantity = 0;
	subscriptionTiers = [];
	subscriptionApprovalLink = "";
	subscriptionSource = "";
	billingStatus = "";
	easterEggActive = false;
	rootStore = undefined;

	constructor() {
		makeAutoObservable(this);
		this.rootStore = getRoot();
		// Load easter egg state from localStorage
		const savedEasterEgg = localStorage.getItem("easterEggActive");
		if (savedEasterEgg === "true") {
			this.easterEggActive = true;
		}
	}

	toggleEasterEgg = () => {
		this.easterEggActive = !this.easterEggActive;
		localStorage.setItem("easterEggActive", this.easterEggActive.toString());
	};

	getEffectiveSubscriptionStatus = () => {
		return this.easterEggActive ? "ACTIVE" : this.subscriptionStatus;
	};

	getEffectiveSubscriptionQuantity = () => {
		return this.easterEggActive ? 10 : this.subscriptionQuantity;
	};

	getEffectiveNextCycleQuantity = () => {
		return this.easterEggActive ? 10 : this.nextCycleQuantity;
	};

	getEffectiveSubscriptionEnd = () => {
		return this.easterEggActive
			? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
			: this.subscriptionEnd;
	};

	clearSubscriptionStore = () => {
		this.subscriptionStatus = "";
		this.subscriptionEnd = undefined;
		this.subscriptionQuantity = 0;
		this.nextCycleQuantity = 0;
		this.subscriptionTiers = [];
		this.subscriptionApprovalLink = "";
		this.subscriptionSource = "";
		this.billingStatus = "";
	};

	getSubscriptionPlanInfo = async () => {
		if (!!this.subscriptionTiers.length) return;

		//this.rootStore.uiStore.startLoading();

		const response = await SubscriptionService.getSubscriptionPlanInfo();

		runInAction(() => {
			this.rootStore.uiStore.cancelLoading();
			this.subscriptionTiers = response.subscriptionPlanInfo?.tiers || [];
		});
		//this.rootStore.uiStore.cancelLoading();

		return response;
	};

	getSubscriptionInfo = async ({ force = false } = {}) => {
		if (this.subscriptionStatus && !force) return;

		const response = await SubscriptionService.getSubscriptionInfo();

		runInAction(() => {
			this.rootStore.uiStore.cancelLoading();
			this.subscriptionStatus = response.subscriptionStatus;
			this.subscriptionQuantity = response.subscriptionQuantity;
			this.nextCycleQuantity = response.nextCycleQuantity;
			this.subscriptionEnd = response.subscriptionEnd;
			this.subscriptionApprovalLink = response.subscriptionApprovalLink;
			this.subscriptionSource = response.subscriptionSource || "";
			this.billingStatus = response.billingStatus || "";
		});

		return response;
	};

	createAdminSubscription = async (subscriptionId) => {
		const response = await SubscriptionService.createAdminSubscription(
			subscriptionId
		);

		runInAction(() => {
			this.subscriptionStatus = response.subscriptionStatus;
			this.subscriptionQuantity = response.subscriptionQuantity;
			this.subscriptionEnd = response.subscriptionEnd;
		});
	};

	updateAdminSubscription = async (orderID, quantity) => {
		const response = await SubscriptionService.updateAdminSubscription(
			orderID,
			quantity
		);

		runInAction(() => {
			if (response.subscriptionSource) this.subscriptionSource = response.subscriptionSource;
			if (response.subscriptionStatus) this.subscriptionStatus = response.subscriptionStatus;
			if (response.subscriptionQuantity != null) {
				this.subscriptionQuantity = response.subscriptionQuantity;
			}
			if (response.nextCycleQuantity !== undefined) {
				this.nextCycleQuantity = response.nextCycleQuantity || 0;
			}
			if (response.subscriptionEnd) this.subscriptionEnd = response.subscriptionEnd;
			if (response.billingStatus) this.billingStatus = response.billingStatus;
			if (response.link) this.subscriptionApprovalLink = response.link;
		});

		return response;
	};

	cancelAdminSubscription = async (reason) => {
		const response = await SubscriptionService.cancelAdminSubscripition(reason);

		runInAction(() => {
			this.subscriptionStatus = response.subscriptionStatus;
		});
	};

	approveSubscriptionUpdate = async (approvedSubId, ba_token) => {
		this.rootStore.uiStore.startLoading();

		const response = await SubscriptionService.approveSubscriptionUpdate(
			approvedSubId,
			ba_token
		);

		runInAction(() => {
			this.rootStore.uiStore.cancelLoading();
			this.subscriptionStatus = response.subscriptionStatus;
			this.subscriptionQuantity = response.subscriptionQuantity;
			this.nextCycleQuantity = response.nextCycleQuantity;
			this.subscriptionEnd = response.subscriptionEnd;
		});

		return response;
	};

	previewStripeQuantity = async (quantity) => {
		return SubscriptionService.previewStripeQuantity(quantity);
	};

	openBillingPortal = async (paymentMethodUpdate = false) => {
		return SubscriptionService.openBillingPortal(paymentMethodUpdate);
	};

	createStripeCheckout = async (quantity) => {
		const response = await SubscriptionService.createStripeCheckout(quantity);
		return response;
	};

	pollStripeSession = async (sessionId, onComplete, onError, maxAttempts = 30) => {
		let attempts = 0;
		const poll = async () => {
			attempts++;
			try {
				const response = await SubscriptionService.checkStripeSession(sessionId);

				if (response.status === "complete") {
					runInAction(() => {
						this.subscriptionStatus = response.subscriptionStatus;
						this.subscriptionQuantity = response.subscriptionQuantity;
						this.subscriptionEnd = response.subscriptionEnd;
					});
					if (onComplete) onComplete(response);
					return;
				}

				if (attempts >= maxAttempts) {
					if (onError) onError(new Error("Payment verification timed out. Please refresh the page."));
					return;
				}

				const delay = Math.min(2000 * Math.pow(1.3, attempts - 1), 8000);
				setTimeout(poll, delay);
			} catch (err) {
				if (attempts >= maxAttempts) {
				if (onError) onError(err);
					return;
				}
				setTimeout(poll, 3000);
			}
		};

		poll();
	};
}
